import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { ReactNode } from 'react';

import { CopyTradeStrategyMode, copyTradeStrategyModes } from '@flipster/apis/v1/copy-trade/types';
import { FormattedMessage } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { copyTradingSettingsBunja } from '../../state';

const DropdownSelect = createDropdownSelect<CopyTradeStrategyMode>();

const modeLabelMap: Record<CopyTradeStrategyMode, { label: ReactNode; subLabel: ReactNode }> = {
  COPY_ALL_EXISTING: {
    label: <FormattedMessage id='Copy.r251107.Copy_all_existing_positions' />,
    subLabel: <FormattedMessage id='Copy.r251107.Copy_all_existing_positions_at_market_price' />,
  },
  COPY_BETTER_ENTRIES: {
    label: <FormattedMessage id='Copy.r251107.Copy_better_entries_existing_positions' />,
    subLabel: <FormattedMessage id='Copy.r251107.Copy_only_enter_at_better_price_than_master' />,
  },
  COPY_NEW_POSITIONS_ONLY: {
    label: <FormattedMessage id='Copy.r251107.Copy_new_positions_only' />,
    subLabel: <FormattedMessage id='Copy.r251107.Master_existing_positions_will_not_copied' />,
  },
};

export const CopyModeDropdown = () => {
  const { copyModeAtom } = useBunja(copyTradingSettingsBunja);
  const [copyMode, setCopyMode] = useAtom(copyModeAtom);
  return (
    <DropdownSelect.Root selected={copyMode} onChange={setCopyMode}>
      <DropdownSelect.Trigger css={{ w: '100%' }}>
        <div
          className={hstack({
            justify: 'space-between',
            p: 16,
            borderColor: 'border.default.level-2',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 8,
          })}
        >
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            {modeLabelMap[copyMode].label}
          </p>
          <CaretDownIcon
            size={16}
            color={token('colors.content.default.level-3')}
            className={css({
              transition: 'transform 200ms',
              '[aria-expanded=true] &': {
                transform: 'rotate(180deg)',
              },
            })}
          />
        </div>
      </DropdownSelect.Trigger>
      <DropdownSelect.Select portal>
        {copyTradeStrategyModes.map((mode) => (
          <DropdownSelect.Item
            key={mode}
            value={mode}
            label={modeLabelMap[mode].label}
            subLabel={modeLabelMap[mode].subLabel}
          />
        ))}
      </DropdownSelect.Select>
    </DropdownSelect.Root>
  );
};
